import { appearanceFields, productCardStyleField } from "./_shared";
/**
 * The ported EasyOrders layouts that lead with a promo banner. Every banner
 * field gates on this list AND on `showBanner`, not on `showBanner` alone:
 * `showBanner` defaults to `true`, and `withFieldDefaults` injects that default
 * whether or not the toggle is itself visible — so a `showBanner`-only gate
 * evaluated true on `standard`/`slider`/`list`/`masonry`/`featured` too, and
 * every pre-existing category section sprouted five banner fields it had no way
 * to render.
 */
const BANNER_VARIANTS = [
    "banner-grid",
    "hero-eyebrow",
    "hero-subtitle",
    "hero-split",
    "rows",
];
/**
 * Category section — a seller-curated product row for a single category
 * the seller picks. Multiple instances can be inserted, one per category
 * the seller wants to spotlight on the page. This replaces the old
 * auto-render-every-category behaviour AND the separate `featured-category`
 * schema; both now collapse into this single reusable section.
 */
export const categorySectionsSchema = {
    type: "category-sections",
    name: { ar: "قسم فئة", en: "Category section" },
    description: {
        ar: "صف منتجات من فئة تختارها. ضيف أكتر من قسم عشان تبرز فئات مختلفة في أماكن مختلفة.",
        en: "A product row for a category you pick. Add multiple instances to spotlight different categories anywhere on the page.",
    },
    maxPerTemplate: 6,
    availableOnTemplates: ["home"],
    settings: [
        // The shared product layouts plus `banner-grid`, a ported EasyOrders
        // section (ps-featured: avyro, elite, nature, sharp). Written out rather
        // than spread from productLayoutField() so the union type stays narrow —
        // and so product sections do not inherit a category-only layout.
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "طريقة عرض المنتجات.",
                en: "How the products are laid out.",
            },
            options: [
                { value: "standard", label: { ar: "شبكة", en: "Grid" } },
                { value: "slider", label: { ar: "شرائح", en: "Carousel" } },
                { value: "list", label: { ar: "قائمة", en: "List" } },
                { value: "masonry", label: { ar: "متداخل", en: "Masonry" } },
                { value: "featured", label: { ar: "مميز", en: "Featured" } },
                {
                    value: "banner-grid",
                    label: { ar: "شبكة ببانر", en: "Banner grid" },
                },
                {
                    value: "hero-eyebrow",
                    label: { ar: "بانر جانبي", en: "Side banner" },
                },
                {
                    value: "hero-subtitle",
                    label: { ar: "بانر بوصف", en: "Banner with subtitle" },
                },
                {
                    value: "hero-split",
                    label: { ar: "بطاقة مقسّمة", en: "Split card" },
                },
                { value: "rows", label: { ar: "صفوف", en: "Rows" } },
                // Ported from EasyOrders `ps-plist--split-showcase` (nature family,
                // 2026-07-20). A one-card-at-a-time carousel whose slide is a split
                // panel: a tall portrait photograph beside the product's own copy.
                // Same content model as every other layout here (products of one
                // category), so it is a layout, not a new section type.
                {
                    value: "split-showcase",
                    label: { ar: "عرض مقسّم", en: "Split showcase" },
                },
            ],
            default: "standard",
        },
        productCardStyleField(),
        {
            id: "categoryId",
            type: "categoryPicker",
            single: true,
            label: { ar: "الفئة", en: "Category" },
            helpText: {
                ar: "اختر الفئة اللي عايز تعرض منتجاتها في الصف ده.",
                en: "Pick the category whose products to display in this row.",
            },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "عنوان القسم", en: "Section title" },
            helpText: {
                ar: "سيبه فاضي عشان ياخد اسم الفئة تلقائيًا.",
                en: "Leave blank to use the category name automatically.",
            },
            validate: { max: 60 },
        },
        {
            id: "productsPerSection",
            type: "range",
            label: { ar: "عدد المنتجات في الصف", en: "Products per row" },
            helpText: {
                ar: 'عدد المنتجات اللي تظهر في القسم ده قبل زر "عرض الكل".',
                en: "How many products to show before the View-all CTA.",
            },
            min: 1,
            max: 12,
            step: 1,
            default: 5,
        },
        {
            id: "columnsPerRow",
            type: "range",
            // Desktop-only (see all-products.columnsPerRow): sets the desktop column
            // count; mobile keeps its horizontal-scroll row. Hidden on mobile/tablet
            // tabs so the control isn't shown where the preview doesn't react.
            appliesTo: ["desktop"],
            label: { ar: "عدد الأعمدة", en: "Columns per row" },
            helpText: {
                ar: "عدد الأعمدة على شاشات الكمبيوتر. الموبايل بيفضل تمرير أفقي.",
                en: "Desktop columns. Mobile keeps the horizontal-scroll row.",
            },
            min: 2,
            max: 6,
            step: 1,
            default: 4,
            // A7: slider/list lay out their own way and ignore the column count.
            showIf: { variant: ["standard", "masonry", "featured"] },
        },
        {
            id: "showSectionTitle",
            type: "checkbox",
            label: { ar: "إظهار اسم القسم", en: "Show section title" },
            default: true,
        },
        {
            id: "showViewAll",
            type: "checkbox",
            label: { ar: "إظهار زر «عرض الكل»", en: "Show “View all” button" },
            helpText: {
                ar: "الزر اللي جنب اسم القسم وبيودّي لصفحة القسم كاملة.",
                en: "The button beside the section title that opens the full category.",
            },
            // ON by default: it is the section's only route to the full category, so
            // an existing store must keep it unless the seller says otherwise.
            //
            // The renderer ALREADY hid this button in two cases — when the promo
            // banner carries its own "View All" (a second copy of one action) and on
            // `split-showcase`. Those stay: they prevent a duplicate or broken
            // layout, which is not the same question as "does this seller want the
            // button at all". So the button shows only when the seller has left this
            // on AND the layout has somewhere sensible to put it.
            default: true,
        },
        {
            id: "showBanner",
            type: "checkbox",
            label: { ar: "إظهار بانر ترويجي", en: "Show promo banner" },
            helpText: {
                ar: "بانر بصورة وعنوان وزر، بياخد أول خانة في الشبكة.",
                en: "A banner with its own image, title and button, taking the first cell of the grid.",
            },
            // ON by default. The banner IS the defining feature of these ported
            // variants — with it off, picking one renders a plain grid that looks
            // nothing like its source. The seller can still switch it off; this only
            // changes what you get the moment you choose the variant.
            default: true,
            showIf: { variant: BANNER_VARIANTS },
        },
        {
            id: "bannerImage",
            type: "image",
            label: { ar: "صورة البانر", en: "Banner image" },
            helpText: {
                ar: "الحجم المقترح: 640×816 بكسل. اسحب النقطة عشان تختار الجزء اللي يفضل ظاهر.",
                en: "Recommended: 640×816 px. Drag the dot to choose which part stays in view.",
            },
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
            // Same treatment as `hero.image`: the banner is full-bleed art in a fixed
            // 320/408 frame, so `CategoryPromoBanner` force-covers it. `fitLocked`
            // hides the dead cover/contain toggle; the focal dot is what actually
            // matters, since a 4:5 crop of a wide photo usually loses the subject.
            focalPoint: true,
            fitLocked: true,
        },
        {
            id: "bannerEyebrow",
            type: "text",
            i18n: true,
            label: { ar: "سطر صغير فوق عنوان البانر", en: "Banner eyebrow" },
            validate: { max: 30 },
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
        },
        {
            id: "bannerTitle",
            type: "text",
            i18n: true,
            label: { ar: "عنوان البانر", en: "Banner title" },
            validate: { max: 40 },
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
        },
        {
            id: "bannerSubtitle",
            type: "text",
            i18n: true,
            label: { ar: "وصف البانر", en: "Banner subtitle" },
            validate: { max: 60 },
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
        },
        {
            id: "bannerCtaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص زر البانر", en: "Banner button label" },
            validate: { max: 24 },
            default: { ar: "عرض الكل", en: "View all" },
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
        },
        {
            id: "titleStyle",
            type: "select",
            label: { ar: "شكل العنوان", en: "Title style" },
            helpText: {
                ar: "شكل عنوان القسم.",
                en: "How the section title is set.",
            },
            // The five heading treatments measured across their featured-products
            // rows. `eyebrow` is ours and stays the default so existing stores are
            // untouched. See docs/plans/2026-07-19-storefront-section-vocabulary.md.
            options: [
                { value: "eyebrow", label: { ar: "سطر صغير", en: "Small eyebrow" } },
                { value: "plain", label: { ar: "عادي", en: "Plain" } },
                { value: "spaced", label: { ar: "متباعد", en: "Spaced caps" } },
                { value: "bold", label: { ar: "عريض", en: "Bold" } },
                { value: "display", label: { ar: "كبير", en: "Display" } },
                // MEASURED on phoenix-avyro `.ab-plist-title` (2026-07-22): 16px w500
                // UPPERCASE ls 1.28px with a 2px rule under the word. Additive — no
                // existing section can select it, so nothing shifts.
                { value: "rule", label: { ar: "مسطر", en: "Underlined caps" } },
            ],
            default: "eyebrow",
            showIf: { showSectionTitle: true },
        },
        {
            // Audit §2.6 + §2.15: theirs never renders a bare "View all →" text link
            // next to a carousel — the affordance is an outlined pill and the
            // prev/next pair is lifted OUT of the media into the same header row.
            // `link` is ours and stays the default, so every existing section is
            // byte-identical.
            id: "headerControls",
            type: "select",
            label: { ar: "أدوات رأس القسم", en: "Header controls" },
            helpText: {
                ar: "شكل زر «عرض الكل» وأسهم التمرير في رأس القسم.",
                en: "How the View-all control and the carousel arrows are set in the section header.",
            },
            options: [
                { value: "link", label: { ar: "رابط نصي", en: "Text link" } },
                {
                    value: "pill-arrows",
                    label: { ar: "زر بيضاوي + أسهم", en: "Outlined pill + arrows" },
                },
            ],
            default: "link",
            showIf: { variant: ["slider"] },
        },
        {
            // Audit §2.5: their first-cell banner STRETCHES to the grid-row height
            // and carries a dark bottom scrim with the copy at the bottom-start
            // corner. `poster` is the existing centred-overlay rendering and stays
            // the default.
            id: "bannerStyle",
            type: "select",
            label: { ar: "شكل البانر", en: "Banner style" },
            helpText: {
                ar: "بوستر بمحتوى في المنتصف، أو بانر بيملأ الصف بتدرّج داكن من تحت.",
                en: "A centred poster, or a cell that fills the grid row with a dark bottom scrim.",
            },
            options: [
                { value: "poster", label: { ar: "بوستر", en: "Poster" } },
                { value: "scrim-fill", label: { ar: "ملء الصف", en: "Fill row" } },
            ],
            default: "poster",
            showIf: { variant: BANNER_VARIANTS, showBanner: true },
        },
        {
            id: "titleAlign",
            type: "select",
            label: { ar: "محاذاة العنوان", en: "Title alignment" },
            options: [
                { value: "start", label: { ar: "البداية", en: "Start" } },
                { value: "center", label: { ar: "الوسط", en: "Centre" } },
            ],
            default: "start",
            showIf: { showSectionTitle: true },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        // Per-section colors + the "View all" CTA controls (style + hide).
        // `appearanceFields()` already bundles `colorFields()` + `buttonFields()`,
        // so it declares `buttonStyle`/`hideButton` exactly ONCE — a prior extra
        // `...buttonFields()` here declared them twice (duplicate field ids).
        ...appearanceFields(),
    ],
};
//# sourceMappingURL=category-sections.js.map